<script setup lang="ts">
import { addProductToBasket } from '~/utils/addProductToBasket'
import Product from '~/models/Product/Product'
import { useBasketStore } from '~/stores/basket'

const basket = useBasketStore()
const product = ref<Product | null>(null)
const counter = ref(0)

onMounted(() => {
    useNuxtApp().$bus.$on('showMigrateToMobileAppModal', (pr: Product) => {
        const productAmount = basket.productAmounts[pr.id] || 0
        if (productAmount === 0) {
            addProductToBasket({
                product: pr,
                productId: pr.id,
                amount: productAmount + 1,
                showPreBasket: false,
                forceShowPreBasket: false,
            })
        }

        product.value = pr

        useNuxtApp().$bus.$emit('showModal', 'migrate-to-mobile-app-modal')
    })

    useNuxtApp().$bus.$on('hidden', () => {
        counter.value = 0
    })
})

const price = computed(() => {
    if (!product.value) {
        return 0
    }

    const productAmount = basket.productAmounts[product.value.id] || 0

    return (product.value.price - (product.value.mobileAppPrice || product.value.mobileAppPriceForLoggedInUser)) * productAmount
})
</script>

<template>
    <Modal modal-name="migrate-to-mobile-app-modal" modal-class="modal-migrate-to-app" :title="$t('basket.migration.title')">
        <div class="mobile-order__wrapper">
            <div class="mobile-order__title">
                <span class="mobile-order__step">1.</span>
                <span v-html="$t('basket.migration.step1')"></span>
            </div>
            <div class="mobile-order__links">
                <a
                    class="mobile-app__get-app"
                    :href="$t('mobileApp.android.link')"
                    :title="$t('mobileApp.android.alt')"
                    :aria-label="$t('mobileApp.android.alt')"
                    target="_blank"
                >
                    <img
                        :src="`/nuxt-assets/images/homepage/app/${$t('locale')}/get_it_on_google_play.svg?raw`"
                        :alt="$t('mobileApp.android.alt')"
                        width="130"
                        height="40"
                    />
                </a>

                <a
                    class="mobile-app__get-app"
                    :href="$t('mobileApp.ios.link')"
                    :title="$t('mobileApp.ios.alt')"
                    :aria-label="$t('mobileApp.ios.alt')"
                    target="_blank"
                >
                    <img
                        :src="`/nuxt-assets/images/homepage/app/${$t('locale')}/download_on_the_app_store.svg?raw`"
                        :alt="$t('mobileApp.ios.alt')"
                        width="130"
                        height="40"
                    />
                </a>
            </div>
            <div class="mobile-order__title">
                <div class="mobile-order__step">2.</div>
                <div class="mobile-order__tooltip desktop">
                    <span v-html="$t('basket.migration.step2')"></span>
                </div>
                <div class="mobile-order__tooltip mobile">
                    <span v-html="$t('basket.migration.step2Mobile')"></span>
                </div>
            </div>
            <div class="mobile-order__qr">
                <div class="mobile-order__img">
                    <img
                        :src="$t('basket.migration.qrImg', { basketUid: basket.uid })"
                        :alt="$t('basket.migration.qrAlt')"
                        width="170"
                        height="170"
                    />
                </div>
                <a
                    class="btn mobile-order__button"
                    :href="$t('basket.migration.migrateLink', { basketUid: basket.uid })"
                    @click="counter++"
                >
                    <span v-html="$t('basket.migration.migrateCta')"></span>
                </a>
            </div>
            <span v-if="counter > 5" class="mobile-order__info" v-html="$t('basket.migration.appInstalled')"></span>
            <div class="mobile-order__title">
                <div class="mobile-order__step">3.</div>
                <div class="">
                    <span v-html="$t('basket.migration.step3')"></span>
                    <span v-if="price > 0" class="mobile-order__price" v-html="$formatCurrency(price)"></span>
                </div>
            </div>
        </div>
    </Modal>
</template>

<style scoped lang="scss">
.mobile-order {
    &__title {
        font-size: 14px;
        font-weight: 590;
        display: flex;
        gap: 1rem;
        align-items: center;

        @include screen-md {
            font-size: 16px;
        }
    }

    &__step {
        background-color: #fddde2;
        border-radius: 50%;
        width: 24px;
        height: 24px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
    }

    &__tooltip {
        &.desktop {
            display: none;

            @include screen-md {
                display: flex;
            }
        }

        &.mobile {
            display: flex;

            @include screen-md {
                display: none;
            }
        }
    }

    &__tip {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
    }

    &__links {
        display: flex;
        margin: 1rem auto;
    }

    &__qr {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 1rem auto;
    }

    &__img {
        width: 190px;
        height: 190px;
        box-shadow: 0 0.5rem 1rem rgb(0 0 0 / 20%);
        display: none;
        justify-content: center;
        align-items: center;

        @include screen-md {
            display: flex;
        }
    }

    &__button {
        display: flex;
        justify-items: center;
        justify-content: center;
        border: 1px solid $color-cta;
        color: $color-cta;
        border-radius: 100px;
        font-size: 14px;
        line-height: 19px;
        min-width: 220px;
        min-height: 38px;

        @include screen-md {
            display: none;
        }

        &:hover,
        &:focus {
            color: $color-white;
            background: $color-cta;

            .discount-ico {
                background: url(/nuxt-assets/icons/percentage-white.svg) no-repeat 50%;
            }
        }
    }

    &__price {
        margin-left: 0.5rem;
    }

    &__info {
        text-align: center;
        display: block;
        font-size: 14px;
        color: $color-dark;
        margin-bottom: 2rem;
    }
}
</style>
